import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Background from './components/Background';
import AlarmModal from './components/AlarmModal';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import RadioAlarmPage from './pages/RadioAlarmPage';
import VideoAlarmPage from './pages/VideoAlarmPage';
import TimersPage from './pages/TimersPage';
import WorldClockPage from './pages/WorldClockPage';
import MeditationPage from './pages/MeditationPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import NotFoundPage from './pages/NotFoundPage';
import { useCurrentTime } from './hooks/useCurrentTime';
import { useAlarm } from './hooks/useAlarm';
import { useRadioAlarm } from './hooks/useRadioAlarm';
import { useVideoAlarm } from './hooks/useVideoAlarm';
import { useSingleTimer } from './hooks/useSingleTimer';
import { FullscreenProvider, useFullscreen } from './contexts/FullscreenContext';
// 导入调试工具（仅在开发环境中）
if (process.env.NODE_ENV === 'development') {
  import('./utils/debugStorage');
}

function AppContent() {
  const currentTime = useCurrentTime();
  const location = useLocation();
  
  // Google Analytics 页面浏览追踪
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-TN6BCMRV97', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  
  // 基础闹钟
  const basicAlarm = useAlarm(currentTime);
  
  // 收音机闹钟
  const radioAlarm = useRadioAlarm(currentTime);
  
  // 视频闹钟
  const videoAlarm = useVideoAlarm(currentTime);
  
  // 倒计时器
  const timerAlarm = useSingleTimer();

  // 切换页面时，停止不相关页面正在响铃的闹钟（但保持激活状态）
  useEffect(() => {
    // 当不在首页时，停止 basicAlarm 的响铃（但保持激活状态）
    if (location.pathname !== '/' && basicAlarm.alarmRinging) {
      basicAlarm.stopAlarm();
    }
    
    // 当不在 radio 页面时，停止 radioAlarm 的响铃（但保持激活状态）
    if (location.pathname !== '/radio-alarm' && radioAlarm.alarmRinging) {
      radioAlarm.stopAlarm();
    }
    
    // 当不在 video 页面时，停止 videoAlarm 的响铃（但保持激活状态）
    if (location.pathname !== '/video-alarm' && videoAlarm.alarmRinging) {
      videoAlarm.stopAlarm();
    }
  }, [location.pathname, basicAlarm, radioAlarm, videoAlarm]);

  // 检查是否有任何闹钟在响铃
  const anyAlarmRinging = basicAlarm.alarmRinging || radioAlarm.alarmRinging || videoAlarm.alarmRinging || timerAlarm.isRinging;

  // 统一的停止闹钟函数
  const stopAllAlarms = () => {
    if (basicAlarm.alarmRinging) basicAlarm.stopAlarm();
    if (radioAlarm.alarmRinging) radioAlarm.stopAlarm();
    if (videoAlarm.alarmRinging) videoAlarm.stopAlarm();
    if (timerAlarm.isRinging) timerAlarm.stopTimer();
  };

  // 统一的贪睡函数
  const snoozeAllAlarms = () => {
    if (basicAlarm.alarmRinging) basicAlarm.snoozeAlarm();
    if (radioAlarm.alarmRinging) radioAlarm.snoozeAlarm();
    if (videoAlarm.alarmRinging) videoAlarm.snoozeAlarm();
    // 倒计时器不支持贪睡功能，直接停止
    if (timerAlarm.isRinging) timerAlarm.stopTimer();
  };

  const { isFullscreen } = useFullscreen();

  // 确定当前响铃的闹钟类型
  const getAlarmType = () => {
    console.log('getAlarmType called:', {
      videoAlarmRinging: videoAlarm.alarmRinging,
      radioAlarmRinging: radioAlarm.alarmRinging,
      basicAlarmRinging: basicAlarm.alarmRinging,
      timerIsRinging: timerAlarm.isRinging,
      timerUseVideoAlarm: timerAlarm.useVideoAlarm,
      timerVideoId: timerAlarm.videoId
    });
    
    if (videoAlarm.alarmRinging) return 'video';
    if (radioAlarm.alarmRinging) return 'radio';
    if (basicAlarm.alarmRinging) return 'basic';
    if (timerAlarm.isRinging && timerAlarm.useVideoAlarm && timerAlarm.videoId) return 'timer-video';
    if (timerAlarm.isRinging) return 'timer';
    return null;
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900">
        <Background />
        
        {!isFullscreen && <Navigation />}
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 py-12 pt-24">
          <Routes>
            <Route path="/" element={<HomePage currentTime={currentTime} basicAlarm={basicAlarm} />} />
            <Route path="/radio-alarm" element={<RadioAlarmPage currentTime={currentTime} radioAlarm={radioAlarm} />} />
            <Route path="/video-alarm" element={<VideoAlarmPage currentTime={currentTime} videoAlarm={videoAlarm} />} />
            <Route path="/timers" element={<TimersPage />} />
            <Route path="/world-clock" element={<WorldClockPage />} />
            <Route path="/meditation" element={<MeditationPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>

        {!isFullscreen && <Footer />}

        {anyAlarmRinging && (
          <AlarmModal 
            stopAlarm={stopAllAlarms} 
            snoozeAlarm={snoozeAllAlarms}
            alarmType={getAlarmType()}
            videoAlarm={videoAlarm.alarmRinging ? videoAlarm : null}
            timerVideoAlarm={timerAlarm.isRinging && timerAlarm.useVideoAlarm && timerAlarm.videoId ? timerAlarm : null}
            basicAlarm={basicAlarm.alarmRinging ? basicAlarm : null}
            radioAlarm={radioAlarm.alarmRinging ? radioAlarm : null}
          />
        )}
        </div>
    </HelmetProvider>
  );
}

function AppWithRouter() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <FullscreenProvider>
        <AppWithRouter />
      </FullscreenProvider>
    </ErrorBoundary>
  );
}